import { useState } from "react"
import { motion } from "framer-motion"
import { Layout } from "@/components/Layout"
import { FacultyCard, StatCard } from "@/components/Cards"
import { facultyMembers, departments } from "@/lib/index"
import { fadeInUp, staggerContainer, staggerItem } from "@/lib/motion"
import { IMAGES } from "@/assets/images"
import { Users, GraduationCap, Award } from "lucide-react"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

export default function Faculty() {
  const [selectedDepartment, setSelectedDepartment] = useState<string>("all")

  const filteredMembers =
    selectedDepartment === "all"
      ? facultyMembers
      : facultyMembers.filter(
          (member) => member.department === selectedDepartment
        )

  const stats = [
    { label: "عضو هيئة تدريس", value: 50, icon: Users },
    { label: "حاصلون على ماجستير/دكتوراه", value: 85, icon: GraduationCap },
    { label: "متوسط سنوات الخبرة", value: 12, icon: Award },
  ]

  return (
    <Layout>
      <div className="min-h-screen">
        <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={IMAGES.PROFESSOR_3}
              alt="Faculty Hero"
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
          </div>

          <motion.div
            className="relative z-10 text-center px-4"
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-4 text-foreground">
              هيئة التدريس
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              نخبة من الأساتذة والخبراء الأكاديميين المتميزين
            </p>
          </motion.div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              {stats.map((stat) => (
                <motion.div key={stat.label} variants={staggerItem}>
                  <StatCard {...stat} />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <motion.div
              className="text-center mb-12"
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold mb-4">أعضاء هيئة التدريس</h2>
              <p className="text-muted-foreground text-lg mb-8">
                تعرف على الكادر الأكاديمي المتميز في كلية بوابة التكنولوجيا
              </p>

              <div className="flex justify-center">
                <Select
                  value={selectedDepartment}
                  onValueChange={setSelectedDepartment}
                >
                  <SelectTrigger className="w-[280px]">
                    <SelectValue placeholder="اختر القسم" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">جميع الأقسام</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.nameAr}>
                        {dept.nameAr}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              {filteredMembers.map((member) => (
                <motion.div key={member.id} variants={staggerItem}>
                  <FacultyCard member={member} />
                </motion.div>
              ))}
            </motion.div>

            {filteredMembers.length === 0 && (
              <motion.div
                className="text-center py-16"
                variants={fadeInUp}
                initial="hidden"
                animate="visible"
              >
                <p className="text-muted-foreground text-lg">
                  لا يوجد أعضاء هيئة تدريس في هذا القسم حالياً
                </p>
              </motion.div>
            )}
          </div>
        </section>

        <section className="py-20 bg-primary/5">
          <div className="container mx-auto px-4">
            <motion.div
              className="max-w-4xl mx-auto text-center"
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                <div className="relative h-64 rounded-2xl overflow-hidden">
                  <img
                    src={IMAGES.PROFESSOR_5}
                    alt="Faculty Teaching"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="relative h-64 rounded-2xl overflow-hidden">
                  <img
                    src={IMAGES.PROFESSOR_6}
                    alt="Faculty Research"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <h2 className="text-3xl font-bold mb-6">
                التميز الأكاديمي والبحث العلمي
              </h2>
              <p className="text-muted-foreground text-lg mb-8">
                يتمتع أعضاء هيئة التدريس في كلية بوابة التكنولوجيا بخبرات أكاديمية
                ومهنية واسعة، ويساهمون بشكل فعال في البحث العلمي والتطوير الأكاديمي.
                نحن ملتزمون بتوفير بيئة تعليمية متميزة تدعم نجاح الطلاب وتطورهم
                المهني.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-right">
                <div className="bg-card p-6 rounded-xl border border-border">
                  <h3 className="font-semibold text-lg mb-2">التدريس المتميز</h3>
                  <p className="text-muted-foreground text-sm">
                    استخدام أحدث أساليب التدريس والتقنيات التعليمية الحديثة
                  </p>
                </div>
                <div className="bg-card p-6 rounded-xl border border-border">
                  <h3 className="font-semibold text-lg mb-2">البحث العلمي</h3>
                  <p className="text-muted-foreground text-sm">
                    المساهمة في الأبحاث العلمية والمشاريع البحثية المتقدمة
                  </p>
                </div>
                <div className="bg-card p-6 rounded-xl border border-border">
                  <h3 className="font-semibold text-lg mb-2">الإرشاد الأكاديمي</h3>
                  <p className="text-muted-foreground text-sm">
                    توجيه ودعم الطلاب في مسيرتهم الأكاديمية والمهنية
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  )
}
